import type {
  BulkOrderPreviewCell,
  BulkOrderPreviewVariation,
  VariationCause,
  VariationDirection,
} from '@/types/api/bulk-order-preview.types';

/**
 * The Allocate grid's arithmetic, over reference data the server supplied.
 *
 * 🔴 **Why a client computes money at all, when FR-44 says it does not.** DL-130 (A.3a's surface
 * gate, 2026-09-14) replaced a preview call per debounced keystroke with **two per bulk order** —
 * one entering Allocate for the reference data, one entering Review for the authoritative figures.
 * Per-keystroke was measured at roughly **120 GB of MongoDB reads for a single 100-site order**,
 * because every call re-reads each allocated site's ~3 MB bill. Between those two calls the grid
 * has to move as the user types, so this module does the arithmetic.
 *
 * The rule that survived is narrower than "no logic on the front end" and is the one that matters:
 * **nothing here is ever sent back to the server, and nothing here invents a figure it was given
 * no basis for.** `POST /bulk-orders` receives the raw grid the user typed; placement recomputes
 * every figure server-side; PE-686's pre-flight re-validates before anything is written. Every
 * threshold, rate and budget below is read from the preview response by path.
 */

/** Stands in for a proposed line in a cell key, which has no BOQ line id of its own. */
export const PROPOSED_LINE_KEY = '@proposed';

/**
 * Addresses one preview cell.
 *
 * Context: the BOQ line is part of the key, not just the site and item — a site legitimately binds
 * one item to several of its own lines, which is the whole reason the table nests.
 */
export const previewCellKey = (
  siteId: string,
  itemId: string,
  boqLineItemId?: string | null,
): string => `${siteId}|${itemId}|${boqLineItemId ?? PROPOSED_LINE_KEY}`;

/** Addresses one `(site, item)` pair in the preview cache (DL-130). */
export const previewCacheKey = (siteId: string, itemId: string): string => `${siteId}|${itemId}`;

/** A BOQ line this order would add to a site's bill, as the new-line form holds it. */
export interface ProposedLineDraft {
  name: string;
  specification: string;
  sectionName: string;
  /** Optional: a line can sit directly under a section. Null where the bill uses no subsections. */
  subSectionName: string | null;
}

/**
 * One row of the grid: a site's binding to one BOQ line, and what the user typed against it.
 *
 * Context: `quantity` is `null` for an untouched field and a number once typed, but **no behaviour
 * distinguishes a blank from a typed `0`** (PE-679) — every consumer reads it through
 * {@link bindingQuantity}. The distinction is kept only so an empty input renders empty rather
 * than pre-filled with a zero nobody entered.
 */
export interface AllocationBinding {
  /**
   * Stable row identity, independent of which line it currently points at.
   *
   * Context: `getRowId` needs a key that survives re-binding through the picker, and the BOQ line
   * id cannot serve — an unbound row has none, and two rows at one site are legitimately unbound
   * at the same time.
   */
  id: string;
  boqLineItemId: string | null;
  boqLineName: string | null;
  proposedLine: ProposedLineDraft | null;
  quantity: number | null;
}

/** A blank field and a typed zero are the same allocation: none. */
export const bindingQuantity = (binding: AllocationBinding): number => binding.quantity ?? 0;

/** Whether a row carries an allocation at all — the "already has a quantity" test (PE-681). */
export const isAllocated = (binding: AllocationBinding): boolean => bindingQuantity(binding) > 0;

/**
 * Every figure one row renders, resolved from the typed quantity and the row's reference cell.
 *
 * `basis` absent means the row is **paused**: its reference data never arrived, so nothing can be
 * entered against it until a retry succeeds. That is distinct from *pending*, which is a figure
 * being recalculated, and from *zero*, which is a real value (PE-682).
 */
export interface AllocationRowFigures {
  quantity: number;
  /** `qty × rate`. The one figure that moves as the user types. */
  rowTotal: number;
  remainingBudget: number | null;
  remainingQuantity: number | null;
  /**
   * What is still outstanding on the line **after** the quantity now typed into this row.
   *
   * 🔴 Display arithmetic over a server-supplied threshold, which is exactly what DL-130 moved to
   * the client — `remainingQuantity` less what the row is asking for, floored at zero because a
   * line cannot be under-ordered into a negative outstanding. `remainingQuantity` itself is
   * untouched: it stays the line's own figure, so the fill control can put the row back to it.
   */
  remainingQuantityAfter: number | null;
  /**
   * What is left of the line's **budget** after the quantity now typed into this row — the money
   * counterpart of `remainingQuantityAfter`, and it moves in lockstep with it.
   *
   * 🔴 **It counts down at the LINE'S rate, never at the order's**, and that is the whole reason
   * this can be shown at all beside a variation without double-counting. Ordering Q units consumes
   * `Q × currentLineRate` of what the line was budgeted; what the order actually *pays*,
   * `Q × orderRate`, is a different question, and the difference between the two **is** the
   * variation. So this figure says what is left of the budget, and the variation flag beside it
   * says what goes over it. Neither restates the other.
   *
   * ⚠️ **This is the server's own answer, not a client convention.** After placement the line
   * holds `Spent' = Spent + Q × orderRate` and `Remaining' = remainingQuantity − Q`, and
   * `TotalAmount' = Remaining' × Rate + Spent'` — so `remainingBudget' = TotalAmount' − Spent'` is
   * `(remainingQuantity − Q) × Rate`. The order's rate enters `Spent` and `TotalAmount` together
   * and cancels. This is what the BOQ line will actually read once the order is placed.
   *
   * ⚠️ **Not the thing DL-84 forbids.** That rule is against deriving the remaining *quantity*
   * from money (`floor(remainingBudget / rate)`), which diverges the moment the order rate differs
   * from the line's. This nets money off money on the same axis and leaves `remainingQuantity`
   * alone; the two figures still arrive from the response independently.
   */
  remainingBudgetAfter: number | null;
  currentLineRate: number | null;
  lineTotalBefore: number | null;
  unorderableReason: string | null;
  variation: BulkOrderPreviewVariation | null;
  /**
   * What this row does to its BOQ line's budgeted total — **`after − before`**, positive for an
   * increase.
   *
   * 🔴 **One figure for every cause, not one per cause.** A quantity breach, a rate move and both
   * together are the same arithmetic reached from the line's final state: `max(remaining, qty) ×
   * rate − remainingBudget`. Branching per cause would have produced three formulas that have to
   * agree, and the combined case is exactly where they would not. See {@link netLineMovement}.
   *
   * Null where there is no variation, or no basis to measure one against.
   */
  budgetMovement: number | null;
  isOverQuantity: boolean;
  /** False where no reference cell was found for this binding — the paused row. */
  hasBasis: boolean;
}

/**
 * The net change this row makes to its BOQ line's budgeted total. Positive is an increase.
 *
 * 🔴 **This deliberately differs from `BulkOrderPreviewFormulas.MovementOf`, and only in
 * magnitude.** The server computes
 * `after = max(line.RemainingQuantity, qty) × orderRate + line.Spent` against
 * `before = line.TotalAmount`, which re-rates the **whole outstanding quantity** at the order's
 * rate. That is right for a BOQ variation that updates the line's standing rate — and this flow
 * does not: DL-34 puts the rate-scope decision with the variation approver (S-08.3), so a bulk
 * order's rate applies to **this order only** and the units nobody is ordering keep the rate they
 * were budgeted at.
 *
 * So within the line's outstanding quantity, only the units actually being ordered are re-rated:
 *
 * ```
 * qty ≤ remaining:  qty × (orderRate − lineRate)
 * qty > remaining:  qty × orderRate − remainingBudget
 * ```
 *
 * The second is the server's own figure — past the outstanding quantity the whole budget is
 * consumed and the excess units were never budgeted at all, so the two agree there.
 *
 * ⚠️ **The two formulas always share a SIGN**, which is what makes this safe to diverge on. Within
 * the quantity, `qty × (r − R)` and `remaining × (r − R)` differ by a positive factor; past it they
 * are identical. So `isGating` — the server's verdict, which this client must not reinvent (DL-52)
 * — is unaffected, and only the figure the tooltip states changes. Raised against the preview
 * service: its own movement over-states a within-quantity rate change for the same reason.
 *
 * A proposed line has no line to move, so its net is simply what it costs — always an increase.
 */
export const netLineMovement = (
  quantity: number,
  rate: number,
  cell: BulkOrderPreviewCell,
): number => {
  if (cell.remainingBudget == null || cell.remainingQuantity == null) return quantity * rate;

  if (cell.currentLineRate != null && quantity <= cell.remainingQuantity) {
    return quantity * (rate - cell.currentLineRate);
  }

  return quantity * rate - cell.remainingBudget;
};

/**
 * Why a variation is raised, or `None`.
 *
 * ⚠️ **The precedence is the server's and is not this client's to reorder** (`VariationCause`): a
 * line that does not exist subsumes every other reason, and a quantity past what the line has
 * outstanding is named ahead of a rate move because it describes something nobody budgeted for. A
 * rate and a quantity moving together therefore report `OverBudgetQuantity`, with `direction`
 * carrying the net effect of both.
 */
export const variationCauseFor = (
  quantity: number,
  rate: number,
  cell: BulkOrderPreviewCell,
  rateTolerance: number,
): VariationCause => {
  if (quantity <= 0) return 'None';
  if (cell.proposedLine || cell.boqLineItemId == null) return 'NewOrOffBillLine';
  if (cell.remainingQuantity != null && quantity > cell.remainingQuantity) {
    return 'OverBudgetQuantity';
  }

  // 🔴 Strictly greater than, never `>=`. The platform's test is `IsApproximately`, which is
  // `|self − other| <= within` — so a difference exactly at the tolerance raises nothing (DL-131).
  // The `0.004` example that circulates in the design brief and the older artefacts is backwards:
  // at 0.004 against a 0.005 tolerance the rates are approximately equal.
  if (cell.currentLineRate == null) return 'None';

  return Math.abs(rate - cell.currentLineRate) > rateTolerance ? 'RateChange' : 'None';
};

const directionOf = (net: number): VariationDirection => {
  if (net > 0) return 'Increase';
  if (net < 0) return 'Decrease';

  return 'NoChange';
};

/** The variation this row would raise, or `null` where it raises none. */
export const variationFor = (
  quantity: number,
  rate: number,
  cell: BulkOrderPreviewCell,
  rateTolerance: number,
): BulkOrderPreviewVariation | null => {
  const cause = variationCauseFor(quantity, rate, cell, rateTolerance);
  if (cause === 'None') return null;

  const net = netLineMovement(quantity, rate, cell);

  // Gating turns on the direction of the net movement alone, never on the cause — FR-20 raises a
  // variation for several reasons and only an increase holds the order up (DL-52).
  return { cause, direction: directionOf(net), isGating: net > 0 };
};

/**
 * What a line still has outstanding once this row's quantity is counted against it.
 *
 * Floored at zero: ordering past a line's remaining is a variation rather than a negative
 * outstanding, and the over-quantity warning is what says so. Null stays null — a row with no
 * reference cell has no outstanding figure to net down, which is a different statement from zero.
 */
export const remainingAfter = (
  remainingQuantity: number | null,
  quantity: number,
): number | null => (remainingQuantity == null ? null : Math.max(0, remainingQuantity - quantity));

/**
 * What a line has left of its **budget** once this row's quantity is counted against it.
 *
 * The money counterpart of {@link remainingAfter}, and floored for the same reason: ordering past
 * a line's remaining consumes the budget entirely and the excess is a variation, not a negative
 * balance. Null where either figure is missing — a proposed line has no budget to spend down,
 * which is a different statement from having none left.
 *
 * 🔴 **`lineRate`, never the order's rate.** See `AllocationRowFigures.remainingBudgetAfter` for
 * why: what the order pays and what it consumes of the budget are different questions, and their
 * difference is the variation.
 */
export const budgetRemainingAfter = (
  remainingBudget: number | null,
  quantity: number,
  lineRate: number | null,
): number | null => {
  if (remainingBudget == null || lineRate == null) return null;

  return Math.max(0, remainingBudget - quantity * lineRate);
};

/** Every row figure, for a binding whose reference cell was found. */
export const rowFiguresFor = (
  binding: AllocationBinding,
  rate: number,
  cell: BulkOrderPreviewCell | undefined,
  rateTolerance: number,
): AllocationRowFigures => {
  const quantity = bindingQuantity(binding);

  if (!cell) {
    return {
      quantity,
      rowTotal: quantity * rate,
      remainingBudget: null,
      remainingBudgetAfter: null,
      remainingQuantity: null,
      remainingQuantityAfter: null,
      currentLineRate: null,
      lineTotalBefore: null,
      unorderableReason: null,
      /**
       * 🔴 **A proposed line raises a variation with or without a preview cell**, and that is the
       * whole of this branch. The entry call sends no allocations, so a line the officer has just
       * invented has no cell to resolve against — and reading "no cell" as "no variation" left the
       * one row on the grid that is certainly a change to the bill wearing no flag at all.
       *
       * It is knowable without the server: a line that is not on the bill is added to it, which is
       * an increase, which gates. The money is the whole of what the row costs, because a line that
       * does not exist has nothing budgeted to offset it.
       */
      variation: binding.proposedLine
        ? { cause: 'NewOrOffBillLine', direction: 'Increase', isGating: true }
        : null,
      budgetMovement: binding.proposedLine ? quantity * rate : null,
      isOverQuantity: false,
      hasBasis: false,
    };
  }

  const rowTotal = quantity * rate;
  const variation = variationFor(quantity, rate, cell, rateTolerance);

  return {
    quantity,
    rowTotal,
    remainingBudget: cell.remainingBudget ?? null,
    remainingBudgetAfter: budgetRemainingAfter(
      cell.remainingBudget ?? null,
      quantity,
      cell.currentLineRate ?? null,
    ),
    remainingQuantity: cell.remainingQuantity ?? null,
    remainingQuantityAfter: remainingAfter(cell.remainingQuantity ?? null, quantity),
    currentLineRate: cell.currentLineRate ?? null,
    lineTotalBefore: cell.lineTotalBefore ?? null,
    unorderableReason: cell.unorderableReason ?? null,
    variation,
    budgetMovement: variation ? netLineMovement(quantity, rate, cell) : null,
    isOverQuantity: cell.remainingQuantity != null && quantity > cell.remainingQuantity,
    hasBasis: true,
  };
};

/**
 * The quantity "Allocate remaining" and the per-row fill put into a row.
 *
 * 🔴 **DL-84, and the one figure this batch is most likely to get wrong.** It is the BOQ line's
 * **outstanding quantity** — a line of 100 with 30 already ordered contributes 70 — and it is
 * **independent of the rate override**. The design pack computes `floor(remainingBudget / rate)`
 * (`BulkAllocateStep.jsx:949`) and that is a different number the moment the order rate differs
 * from the line's own rate, which is the ordinary case on this screen. It is not built.
 *
 * Returns 0 rather than null where a line has nothing outstanding or no budget to speak of, since
 * a fill of nothing and a fill of zero are the same instruction.
 */
export const fillQuantityFor = (cell: BulkOrderPreviewCell | undefined): number => {
  const remaining = cell?.remainingQuantity;
  if (remaining == null || remaining <= 0) return 0;

  return remaining;
};

/** How many breakdown rows a site's variation tooltip lists before it aggregates the rest. */
export const SITE_VARIATION_ROWS = 3;

/** One line's contribution to what a site's variations do to the BOQ budget. */
export interface SiteVariationLine {
  id: string;
  label: string;
  /** Signed: positive adds to the budget, negative takes off it. */
  amount: number;
}

export interface SiteVariationSummary {
  /** The lines to list, largest movement first whichever way it goes. */
  lines: SiteVariationLine[];
  /** How many lines are folded into the remainder row, and what they come to together. */
  hiddenCount: number;
  hiddenAmount: number;
  /** One net figure the listed lines and the remainder add up to. */
  total: number;
}

/**
 * What a site's variations do to the BOQ budget, and which lines that came from.
 *
 * 🔴 **One net total, and the rows add up to it** (CPTO, 2026-09-16). Increases and reductions were
 * reported as two separate blocks with a total each, which read as two answers to one question and
 * interleaved badly once a site had both. A reader checking a column wants it to sum.
 *
 * ⚠️ The cost of netting, worth knowing rather than hiding: a site up R 5 000 and down R 4 800 now
 * reads as a R 200 movement, while what it actually holds is a **R 5 000 approval** — a reduction
 * does not pay for an increase. The per-line rows are what keep that visible, which is why the
 * breakdown is not optional dressing on the total.
 *
 * ⚠️ **Sorted by magnitude, not by row order**, because a capped list should show the lines that
 * decide the answer rather than the first three down the bill. The remainder row carries its own
 * sum, so the column still adds up and the reader can check it rather than take it on trust. Four
 * or fewer are all listed: a "+1 more" row is longer than the line it hides.
 */
export const summariseSiteVariations = (
  rows: { id: string; label: string; figures?: AllocationRowFigures }[],
): SiteVariationSummary => {
  const moved = rows
    .flatMap((row) => {
      const amount = row.figures?.budgetMovement ?? 0;

      // Below half a cent is a rounding tail, not a movement anyone can see.
      return Math.abs(amount) >= 0.005 ? [{ id: row.id, label: row.label, amount }] : [];
    })
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));

  const total = moved.reduce((sum, line) => sum + line.amount, 0);

  if (moved.length <= SITE_VARIATION_ROWS + 1) {
    return { lines: moved, hiddenCount: 0, hiddenAmount: 0, total };
  }

  const hidden = moved.slice(SITE_VARIATION_ROWS);

  return {
    lines: moved.slice(0, SITE_VARIATION_ROWS),
    hiddenCount: hidden.length,
    hiddenAmount: hidden.reduce((sum, line) => sum + line.amount, 0),
    total,
  };
};
